import { useState } from "react";
import {
  createUserWithEmailAndPassword,
  signInWithEmailAndPassword,
  sendPasswordResetEmail,
  RecaptchaVerifier,
  signInWithPhoneNumber,
  signOut,
} from "firebase/auth";
import { auth } from "../../../firebaseConfig";
import toast, { Toaster } from "react-hot-toast";
import { NAFED_ROLES } from "./nafedRoles";
import {
  createNafedUserProfile,
  getNafedRedirectPath,
  getNafedUserProfile,
} from "./NafedAuthUtils";

export default function NafedAuthPage() {
  const [mode, setMode] = useState("LOGIN");
  const [loading, setLoading] = useState(false);

  const roleFromUrl = new URLSearchParams(window.location.search).get("role");
  const validRoleFromUrl = NAFED_ROLES.some((item) => item.value === roleFromUrl)
    ? roleFromUrl
    : "";

  const [role, setRole] = useState(validRoleFromUrl || "PACS");
  const [name, setName] = useState("");
  const [organizationName, setOrganizationName] = useState("");
  const [state, setState] = useState("Gujarat");
  const [district, setDistrict] = useState("");

  const [email, setEmail] = useState("");
  const [password, setPassword] = useState("");

  const [mobile, setMobile] = useState("");
  const [otp, setOtp] = useState("");
  const [confirmation, setConfirmation] = useState(null);

  const getRoleLabel = (roleValue) =>
    NAFED_ROLES.find((item) => item.value === roleValue)?.label || roleValue;

  const goAfterLogin = async (user) => {
    const profile = await getNafedUserProfile(user.uid);

    if (!profile) {
      await signOut(auth);
      toast.error("User profile not found. Please register first.");
      return false;
    }

    if (profile.role !== role) {
      await signOut(auth);
      toast.error(
        `Wrong login portal. This account is registered as ${getRoleLabel(
          profile.role
        )}. Please select ${getRoleLabel(profile.role)}.`
      );
      return false;
    }

    if (profile.status !== "APPROVED") {
      await signOut(auth);
      toast.error("Your registration is pending approval.");
      return false;
    }

    if (profile.isActive !== true) {
      await signOut(auth);
      toast.error("Your account is currently inactive. Please contact the administrator.");
      return false;
    }

    window.location.href = getNafedRedirectPath(profile);
    return true;
  };

  const handleRegister = async (e) => {
    e.preventDefault();

    if (!name || !email || !password || !mobile || !role) {
      toast.error("Please fill all required fields.");
      return;
    }

    try {
      setLoading(true);

      const cred = await createUserWithEmailAndPassword(auth, email, password);

      await createNafedUserProfile(cred.user, {
        name,
        email,
        mobile,
        role,
        state,
        district,
        organizationName,
      });

      toast.success(
        role === "HEAD_OFFICE"
          ? "Registration completed."
          : "Registration submitted. Approval required."
      );

      setMode("LOGIN");
    } catch (e) {
      console.error(e);
      toast.error(e.message || "Registration failed.");
    } finally {
      setLoading(false);
    }
  };

  const handleLogin = async (e) => {
    e.preventDefault();

    try {
      setLoading(true);
      const cred = await signInWithEmailAndPassword(auth, email, password);
      await goAfterLogin(cred.user);
    } catch (e) {
      console.error(e);
      toast.error(e.message || "Login failed.");
    } finally {
      setLoading(false);
    }
  };

  const handleForgot = async (e) => {
    e.preventDefault();

    if (!email) {
      toast.error("Enter registered email.");
      return;
    }

    try {
      setLoading(true);
      await sendPasswordResetEmail(auth, email);
      toast.success("Password reset link sent to email.");
      setMode("LOGIN");
    } catch (e) {
      console.error(e);
      toast.error(e.message || "Reset failed.");
    } finally {
      setLoading(false);
    }
  };

  const sendOtp = async () => {
    if (!mobile || mobile.length !== 10) {
      toast.error("Enter 10 digit mobile number.");
      return;
    }

    try {
      setLoading(true);

      if (!window.recaptchaVerifier) {
        window.recaptchaVerifier = new RecaptchaVerifier(
          auth,
          "nafed-recaptcha",
          { size: "invisible" }
        );
      }

      const result = await signInWithPhoneNumber(
        auth,
        `+91${mobile}`,
        window.recaptchaVerifier
      );

      setConfirmation(result);
      toast.success("OTP sent.");
    } catch (e) {
      console.error(e);
      toast.error(e.message || "OTP send failed.");
    } finally {
      setLoading(false);
    }
  };

  const verifyOtp = async () => {
    if (!confirmation || !otp) {
      toast.error("Enter OTP.");
      return;
    }

    try {
      setLoading(true);
      const result = await confirmation.confirm(otp);
      await goAfterLogin(result.user);
    } catch (e) {
      console.error(e);
      toast.error(e.message || "OTP verification failed.");
    } finally {
      setLoading(false);
    }
  };

  return (
    <>
      <Toaster position="top-center" />

      <style>{`
        .nafed-auth-page {
          min-height: 100vh;
          background:
            radial-gradient(circle at top left, rgba(22,163,74,.18), transparent 32%),
            linear-gradient(135deg, #eef7ff, #ffffff 45%, #ecfdf5);
          display: grid;
          place-items: center;
          padding: 28px;
          font-family: Inter, "Segoe UI", Arial, sans-serif;
        }

        .auth-shell {
          width: min(1180px, 100%);
          display: grid;
          grid-template-columns: 1fr 460px;
          gap: 28px;
          align-items: stretch;
        }

        .auth-info {
          background: linear-gradient(135deg, #062b55, #073d7a);
          color: white;
          border-radius: 30px;
          padding: 38px;
          box-shadow: 0 30px 80px rgba(6,43,85,.28);
          overflow: hidden;
          position: relative;
        }

        .auth-info:after {
          content: "";
          position: absolute;
          right: -80px;
          bottom: -80px;
          width: 260px;
          height: 260px;
          border-radius: 50%;
          background: rgba(34,197,94,.22);
        }

        .auth-info h1 {
          margin: 0;
          font-size: 42px;
          font-weight: 950;
          line-height: 1.1;
        }

        .auth-info p {
          margin-top: 18px;
          max-width: 650px;
          color: #dbeafe;
          line-height: 1.8;
          font-size: 15px;
        }

        .auth-points {
          margin-top: 30px;
          display: grid;
          grid-template-columns: repeat(2, 1fr);
          gap: 14px;
        }

        .auth-point {
          background: rgba(255,255,255,.1);
          border: 1px solid rgba(255,255,255,.18);
          border-radius: 18px;
          padding: 18px;
          font-weight: 850;
        }

        .auth-card {
          background: rgba(255,255,255,.92);
          border: 1px solid rgba(255,255,255,.9);
          border-radius: 30px;
          padding: 28px;
          box-shadow: 0 30px 80px rgba(15,23,42,.12);
          backdrop-filter: blur(18px);
        }

        .brand {
          display: flex;
          align-items: center;
          gap: 12px;
          margin-bottom: 22px;
        }

        .brand-logo {
          width: 50px;
          height: 50px;
          border-radius: 16px;
          background: #16a34a;
          color: white;
          display: flex;
          align-items: center;
          justify-content: center;
          font-size: 25px;
        }

        .brand h2 {
          margin: 0;
          color: #062b55;
          font-size: 25px;
          font-weight: 950;
        }

        .brand p {
          margin: 2px 0 0;
          color: #64748b;
          font-size: 12px;
          font-weight: 700;
        }

        .tabs {
          display: grid;
          grid-template-columns: repeat(4, 1fr);
          gap: 7px;
          margin-bottom: 20px;
        }

        .tabs button {
          border: 1px solid #dbe4ef;
          background: #f8fafc;
          color: #062b55;
          border-radius: 12px;
          padding: 10px 6px;
          font-size: 11px;
          font-weight: 900;
          cursor: pointer;
        }

        .tabs button.active {
          background: #062b55;
          color: white;
          border-color: #062b55;
        }

        .form {
          display: grid;
          gap: 13px;
        }

        .field label {
          display: block;
          font-size: 11px;
          text-transform: uppercase;
          color: #64748b;
          font-weight: 950;
          letter-spacing: .08em;
          margin-bottom: 6px;
        }

        .field input,
        .field select {
          width: 100%;
          height: 46px;
          border: 1px solid #dbe4ef;
          border-radius: 14px;
          padding: 0 14px;
          outline: none;
          background: white;
          color: #071f45;
          font-weight: 750;
        }

        .main-btn {
          height: 50px;
          border: 0;
          border-radius: 14px;
          background: #062b55;
          color: white;
          font-weight: 950;
          cursor: pointer;
          margin-top: 4px;
        }

        .main-btn.green {
          background: #16a34a;
        }

        .muted {
          color: #64748b;
          font-size: 12px;
          line-height: 1.6;
          text-align: center;
        }

        .back-link {
          display: block;
          text-align: center;
          margin-top: 14px;
          color: #062b55;
          font-weight: 900;
          text-decoration: none;
        }

        @media(max-width: 980px) {
          .auth-shell {
            grid-template-columns: 1fr;
          }
        }
      `}</style>

      <div className="nafed-auth-page">
        <div className="auth-shell">
          <section className="auth-info">
            <h1>NAFED Universal Login & Registration</h1>
            <p>
              One digital access system for Head Office, State Branch, PMC,
              PACS, Banks, CA, Contractors, Engineers and Accounts. Role-wise
              approval and dashboard redirection will be managed automatically.
            </p>

            <div className="auth-points">
              <div className="auth-point">âœ… Role-wise Registration</div>
              <div className="auth-point">âœ… Approval Based Access</div>
              <div className="auth-point">âœ… OTP Login Support</div>
              <div className="auth-point">âœ… Dashboard Auto Redirect</div>
            </div>
          </section>

          <section className="auth-card">
            <div className="brand">
              <div className="brand-logo">ðŸŒ¾</div>
              <div>
                <h2>NAFED Portal</h2>
                <p>Worldâ€™s Largest Grain Storage Plan</p>
              </div>
            </div>

            <div className="tabs">
              <button
                type="button"
                className={mode === "LOGIN" ? "active" : ""}
                onClick={() => setMode("LOGIN")}
              >
                Login
              </button>

              <button
                type="button"
                className={mode === "REGISTER" ? "active" : ""}
                onClick={() => setMode("REGISTER")}
              >
                Register
              </button>

              <button
                type="button"
                className={mode === "OTP" ? "active" : ""}
                onClick={() => setMode("OTP")}
              >
                OTP
              </button>

              <button
                type="button"
                className={mode === "FORGOT" ? "active" : ""}
                onClick={() => setMode("FORGOT")}
              >
                Forgot
              </button>
            </div>

            {mode === "LOGIN" && (
              <form className="form" onSubmit={handleLogin}>
                <div className="field">
                  <label>Login As</label>
                  <select value={role} onChange={(e) => setRole(e.target.value)}>
                    {NAFED_ROLES.map((r) => (
                      <option key={r.value} value={r.value}>
                        {r.label}
                      </option>
                    ))}
                  </select>
                </div>

                <div className="field">
                  <label>Email</label>
                  <input value={email} onChange={(e) => setEmail(e.target.value)} />
                </div>

                <div className="field">
                  <label>Password</label>
                  <input
                    type="password"
                    value={password}
                    onChange={(e) => setPassword(e.target.value)}
                  />
                </div>

                <button className="main-btn" disabled={loading}>
                  {loading ? "Please wait..." : "Login"}
                </button>
              </form>
            )}

            {mode === "REGISTER" && (
              <form className="form" onSubmit={handleRegister}>
                <div className="field">
                  <label>Role</label>
                  <select value={role} onChange={(e) => setRole(e.target.value)}>
                    {NAFED_ROLES.map((r) => (
                      <option key={r.value} value={r.value}>
                        {r.label}
                      </option>
                    ))}
                  </select>
                </div>

                <div className="field">
                  <label>Full Name</label>
                  <input value={name} onChange={(e) => setName(e.target.value)} />
                </div>

                <div className="field">
                  <label>Organization Name</label>
                  <input
                    value={organizationName}
                    onChange={(e) => setOrganizationName(e.target.value)}
                  />
                </div>

                <div className="field">
                  <label>Mobile</label>
                  <input
                    value={mobile}
                    onChange={(e) =>
                      setMobile(e.target.value.replace(/\D/g, "").slice(0, 10))
                    }
                  />
                </div>

                <div className="field">
                  <label>Email</label>
                  <input value={email} onChange={(e) => setEmail(e.target.value)} />
                </div>

                <div className="field">
                  <label>Password</label>
                  <input
                    type="password"
                    value={password}
                    onChange={(e) => setPassword(e.target.value)}
                  />
                </div>

                <div className="field">
                  <label>State</label>
                  <input value={state} onChange={(e) => setState(e.target.value)} />
                </div>

                <div className="field">
                  <label>District</label>
                  <input
                    value={district}
                    onChange={(e) => setDistrict(e.target.value)}
                  />
                </div>

                <button className="main-btn green" disabled={loading}>
                  {loading ? "Submitting..." : "Register"}
                </button>

                <p className="muted">
                  After registration, approval may be required before dashboard
                  access is enabled.
                </p>
              </form>
            )}

            {mode === "OTP" && (
              <div className="form">
                <div className="field">
                  <label>Login As</label>
                  <select value={role} onChange={(e) => setRole(e.target.value)}>
                    {NAFED_ROLES.map((r) => (
                      <option key={r.value} value={r.value}>
                        {r.label}
                      </option>
                    ))}
                  </select>
                </div>

                <div className="field">
                  <label>Mobile Number</label>
                  <input
                    value={mobile}
                    onChange={(e) =>
                      setMobile(e.target.value.replace(/\D/g, "").slice(0, 10))
                    }
                  />
                </div>

                <button type="button" className="main-btn" onClick={sendOtp}>
                  Send OTP
                </button>

                <div className="field">
                  <label>OTP</label>
                  <input value={otp} onChange={(e) => setOtp(e.target.value)} />
                </div>

                <button type="button" className="main-btn green" onClick={verifyOtp}>
                  Verify OTP
                </button>

                <div id="nafed-recaptcha"></div>
              </div>
            )}

            {mode === "FORGOT" && (
              <form className="form" onSubmit={handleForgot}>
                <div className="field">
                  <label>Registered Email</label>
                  <input value={email} onChange={(e) => setEmail(e.target.value)} />
                </div>

                <button className="main-btn" disabled={loading}>
                  Send Reset Link
                </button>
              </form>
            )}

            <a className="back-link" href="/nafed">
              â† Back to NAFED Home
            </a>
          </section>
        </div>
      </div>
    </>
  );
}